import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, getAuthHeader } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { ScanLine, Upload, RefreshCw, Save, Lock, AlertTriangle } from "lucide-react";
import { ScanReviewTable } from "@/components/ScanReviewTable";

const ACCEPT = "image/jpeg,image/png,image/webp,image/heic,image/heif,application/pdf";

const UploadStep = ({ files, addFiles, removeFile, busy, onRead }) => (
  <div className="space-y-4">
    <label htmlFor="scan-weights-file"
      className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-violet-300 rounded-lg p-8 cursor-pointer hover:bg-violet-50 transition-colors"
      data-testid="scan-weights-dropzone">
      <Upload className="w-6 h-6 text-violet-500" />
      <span className="text-sm font-medium text-slate-700">
        {files.length ? "Add more pages" : "Choose photos or a PDF of the filled weight sheet"}
      </span>
      <span className="text-xs text-slate-500">
        JPG · PNG · WEBP · HEIC · PDF — up to 10 pages, 15 MB each. Multi-page sheets welcome.
      </span>
      <input id="scan-weights-file" type="file" accept={ACCEPT} multiple className="hidden"
        data-testid="scan-weights-file-input"
        onChange={(e) => { addFiles(Array.from(e.target.files || [])); e.target.value = ""; }} />
    </label>
    {files.length > 0 && (
      <div className="flex flex-wrap gap-1.5" data-testid="scan-weights-file-list">
        {files.map((f, i) => (
          <span key={`${f.name}-${i}`} data-testid={`scan-weights-file-chip-${i}`}
            className="inline-flex items-center gap-1 bg-violet-50 border border-violet-200 text-violet-800 rounded-full px-2 py-0.5 text-xs">
            Page {i + 1}: {f.name}
            <button type="button" onClick={() => removeFile(i)}
              data-testid={`scan-weights-file-remove-${i}`}
              className="text-violet-400 hover:text-violet-700 font-bold">×</button>
          </span>
        ))}
      </div>
    )}
    <Button className="w-full bg-violet-600 hover:bg-violet-700" disabled={!files.length || busy}
      onClick={onRead} data-testid="scan-weights-read-btn">
      {busy ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Reading handwriting…</>
            : <><ScanLine className="w-4 h-4 mr-2" /> Read {files.length > 1 ? `${files.length} pages` : "weights"} with AI</>}
    </Button>
  </div>
);

export const ScanWeightsDialog = ({ entry, onClose, onSaved }) => {
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [rows, setRows] = useState([]);
  const [overwrite, setOverwrite] = useState(false);
  const [lockPwd, setLockPwd] = useState(null);

  if (!entry) return null;
  const addFiles = (more) => setFiles(prev => [...prev, ...more].slice(0, 10));
  const removeFile = (i) => setFiles(prev => prev.filter((_, j) => j !== i));
  const reset = () => { setFiles([]); setResult(null); setRows([]); setOverwrite(false); setLockPwd(null); };
  const close = () => { reset(); onClose(); };

  const read = async () => {
    setBusy(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      const r = await axios.post(`${API}/packing-list/history/${entry.id}/scan-weights`, fd,
        { headers: { ...getAuthHeader(), "Content-Type": "multipart/form-data" }, timeout: 180000 });
      setResult(r.data);
      setRows(r.data.rows.map((x) => ({ ...x, weight: x.weight ?? "", packer: x.packer ?? "" })));
      toast.success(`Read ${r.data.read_count} weight(s) from ${r.data.pages || 1} page(s) — please review before saving`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not read the sheet");
    } finally { setBusy(false); }
  };

  const willSave = rows.filter((r) => r.weight !== "" && (overwrite || !r.current_weight));

  const save = async () => {
    setBusy(true);
    try {
      const payload = { overwrite, rows: rows.map((r) => ({
        tracking: r.tracking, weight: r.weight === "" ? null : r.weight, packer: r.packer })) };
      const r = await axios.post(`${API}/packing-list/history/${entry.id}/scan-weights/apply`,
        payload, { headers: getAuthHeader() });
      const { applied, skipped, unmatched, packers_saved } = r.data;
      toast.success(`Saved ${applied.length} weight(s), ${packers_saved} packer(s)` +
        (skipped.length ? ` · ${skipped.length} skipped (already weighed)` : "") +
        (unmatched.length ? ` · ${unmatched.length} not in Database A` : ""));
      onSaved?.();
      close();
    } catch (e) {
      const msg = e.response?.data?.detail || "Save failed";
      if (e.response?.status === 403 && /locked/i.test(msg)) setLockPwd("");
      else toast.error(msg);
    } finally { setBusy(false); }
  };

  const unlockAndSave = async () => {
    try {
      await axios.post(`${API}/weight-edit/unlock`, { password: lockPwd }, { headers: getAuthHeader() });
      setLockPwd(null);
      toast.success("Weight editing unlocked for 10 minutes");
      save();
    } catch (e) { toast.error(e.response?.data?.detail || "Unlock failed"); }
  };

  return (
    <Dialog open={!!entry} onOpenChange={(o) => !o && close()}>
      <DialogContent className={result ? "max-w-5xl" : "max-w-lg"} data-testid="scan-weights-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-violet-700">
            <ScanLine className="w-4 h-4" /> Scan-Back Weights — {entry.invoice_no}
          </DialogTitle>
          <DialogDescription>
            {result ? "Check every amber row, fix any misread number, then save."
                    : "Upload the printed weight sheet after the pack team has written weights and initials."}
          </DialogDescription>
        </DialogHeader>
        {!result ? <UploadStep files={files} addFiles={addFiles} removeFile={removeFile} busy={busy} onRead={read} /> : (
          <>
            <ScanReviewTable rows={rows} setRows={setRows} overwrite={overwrite} />
            {result.unmatched?.length > 0 && (
              <div className="text-xs bg-amber-50 border border-amber-200 rounded p-2 text-amber-800" data-testid="scan-weights-unmatched">
                <AlertTriangle className="w-3.5 h-3.5 inline mr-1" />
                {result.unmatched.length} reading(s) could not be matched to a parcel:{" "}
                {result.unmatched.map((u) => `${u.tracking || `Sr ${u.sr_no ?? "?"}`} = ${u.weight ?? "—"} g`).join(", ")}
              </div>
            )}
            {lockPwd !== null && (
              <div className="flex items-center gap-2 bg-slate-50 border rounded p-2" data-testid="scan-weights-unlock">
                <Lock className="w-4 h-4 text-slate-500" />
                <span className="text-xs text-slate-600">Overwriting weights needs your password (unlocks 10 min):</span>
                <Input type="password" className="h-8 w-48" value={lockPwd} data-testid="scan-weights-unlock-input"
                  onChange={(e) => setLockPwd(e.target.value)} />
                <Button size="sm" onClick={unlockAndSave} data-testid="scan-weights-unlock-btn">Unlock & Save</Button>
              </div>
            )}
            <DialogFooter className="sm:justify-between items-center gap-2">
              <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <Checkbox checked={overwrite} onCheckedChange={(v) => setOverwrite(!!v)}
                  data-testid="scan-weights-overwrite" />
                Overwrite existing weights
              </label>
              <div className="flex gap-2">
                <Button variant="ghost" onClick={reset} data-testid="scan-weights-rescan">Scan another</Button>
                <Button className="bg-violet-600 hover:bg-violet-700" disabled={busy}
                  onClick={save} data-testid="scan-weights-save">
                  {busy ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save {willSave.length} weight(s)
                </Button>
              </div>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
